import { BaseView } from './BaseView';
import { registerSchema } from './transform';

export class Member {
  Id: number = 0;
  UserName: string = '';
  FirstName: string | null = null;
  IconUrl: string = '';
  IsOnline: boolean = false;
  IsAway: boolean = false;
  Status: string = '';
  PlaceName: string | null = null;
  IsFollowing: boolean = false;
  IsFollower: boolean = false;
  IsLocked: boolean = false;
  IsEvent: boolean = false;
  Age: string = '';
  CountryCode: string = '';
  WeightKg: number | null = null;
  WeightGoalKg: number | null = null;
  HeightCm: number | null = null;
  DistanceKm: number | null = null;
  SocialTags: string[] = [];
  LastConnectionDate: Date = new Date();

  get IsOffline(): boolean {
    return !this.IsOnline && !this.IsAway;
  }

  // API sends DateTime.MinValue (year 1) when the member has no recorded activity;
  // subclasses may skip transform coercion, so tolerate string values too
  get LastSeenDate(): Date | null {
    const value: Date | string = this.LastConnectionDate;
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return !isNaN(date.getTime()) && date.getFullYear() > 2000 ? date : null;
  }

  get PlaceNameShort(): string {
    if (!this.PlaceName) {
      return '';
    }
    const parts = this.PlaceName.split(',');
    return parts[0]?.trim() ?? '';
  }

  get PlaceNameCleaned(): string {
    if (!this.PlaceName) {
      return '';
    }
    
    let cleaned = this.PlaceName.trim();
    
    // Split by commas, trim each part, filter out empty parts, then rejoin
    const parts = cleaned
      .split(',')
      .map(part => part.trim())
      .filter(part => part.length > 0);
    
    return parts.join(', ');
  }

  get PlaceNameShortenedTwoElements(): string {
    if (!this.PlaceName) {
      return '';
    }
    
    let cleaned = this.PlaceName.trim();
    
    // Split by commas, trim each part, filter out empty parts, take only first two elements
    const parts = cleaned
      .split(',')
      .map(part => part.trim())
      .filter(part => part.length > 0)
      .slice(0, 2); // Take only first two elements
    
    return parts.join(', ');
  }

  get FirstNameCleaned(): string | null {
    if (!this.FirstName) {
      return null;
    }
    
    let cleaned = this.FirstName.trim();
    
    // Remove matching brackets/parentheses only if they wrap the entire string
    let changed = true;
    while (changed) {
      changed = false;
      const pairs = [['(', ')'], ['[', ']'], ['{', '}']];
      
      for (const [open, close] of pairs) {
        if (cleaned.startsWith(open) && cleaned.endsWith(close) && cleaned.length > 1) {
          cleaned = cleaned.slice(1, -1).trim();
          changed = true;
          break;
        }
      }
    }
    
    // Return null if empty or equals username
    return (cleaned && cleaned !== this.UserName) ? cleaned : null;
  }
}

export class MembersView extends BaseView {
  MembersBasic: Member[] = [];
}

registerSchema(Member, {
  LastConnectionDate: 'date',
});

registerSchema(MembersView, {
  MembersBasic: [Member],
});
